import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Button, Modal } from 'antd';
import Icon from '@ant-design/icons/lib/components/Icon';
import cx from 'classnames';
import { LoadingOutlined } from '@ant-design/icons';

import {
  actions as userActions,
  selectors as userSelectors
} from '../../../slices/users/list';
import {
  selectors as authSelectors
} from '../../../slices/auth';
import { ListParams } from '../../../slices/utils';
import { Ifilter } from '../filters/interfaces';
import { RootState } from '../../../slices/rootReducer';
import { IUser } from '../../../slices/users/user';
import { ActiveInactiveFilterState } from '../activeInactiveToggle';
import { IUserToRemove } from './IUserToRemove';
import { UserRoles } from '../domain/userRoles';
import { RemoveSeatSelector } from './RemoveSeatSelector';
import { ReactComponent as CrossSVG } from '../../../assets/sightx-icons/svg/x-close.svg';
import { toMonthDayYearFormat } from '../../accounts/account/utils';

import styles from './removeSeats.module.scss';

interface IExternalProps {
  visible: boolean;
  seatsToRemove: number | null;
  usersToSelect: number | null;
  cycleEndDate: number;
  alreadyRemovedUsers: string[];
  handleCancel: () => void;
  handleRequireConfirmation: (users: string[]) => void;
}

interface IDispatchProps {
  getUsers: (params: ListParams<Ifilter[]>) => void;
}

interface IStateProps {
  loadingUsers: boolean;
  users: IUser[];
  loggedUserId: string | null;
}

interface ILocalState {
  selectedUsers: IUserToRemove[];
}

type Props = IExternalProps & IDispatchProps & IStateProps;

class RemoveUsedSeatsModalBase extends Component<Props, ILocalState> {
  constructor(props: Props) {
    super(props);
    this.handleNextAction = this.handleNextAction.bind(this);
    this.toggleToBeRemoved = this.toggleToBeRemoved.bind(this);
    this.state = {
      selectedUsers: []
    };
  }

  componentDidMount(): void {
    this.props.getUsers({
      active: ActiveInactiveFilterState.Active
    });
  }

  componentDidUpdate(prevProps: Props): void {
    const { users, loadingUsers, loggedUserId, alreadyRemovedUsers } = this.props;
    if (prevProps.loadingUsers && !loadingUsers) {
      const availableUsers = users.filter(({ id }) => !alreadyRemovedUsers.includes(id));
      this.setState({
        selectedUsers: availableUsers.map(({ email, roles = [], id }) => ({
          email,
          isOwner: roles.includes(UserRoles.CompanyOwner),
          isSelf: id === loggedUserId,
          toBeRemoved: false,
          id
        }))
      });
    }
  }

  private handleNextAction() {
    const { selectedUsers } = this.state;
    const { handleRequireConfirmation } = this.props;

    const usersToRemove = selectedUsers.reduce<string[]>((users, { id, toBeRemoved }) => {
      if (toBeRemoved) {
        users.push(id);
      }
      return users;
    }, []);

    handleRequireConfirmation(usersToRemove);
  }

  private toggleToBeRemoved(id: string) {
    const { selectedUsers: usersToRemove } = this.state;
    this.setState({
      selectedUsers: usersToRemove.map(user => ({
        ...user,
        toBeRemoved: (user.id === id) ? !user.toBeRemoved : user.toBeRemoved
      }))
    });
  }

  private getSelectedUsersCount() {
    const { selectedUsers } = this.state;
    return selectedUsers.reduce((count, user) => {
      if (user.toBeRemoved) {
        return count + 1;
      }
      return count;
    }, 0);
  }

  private selectedEnoughUsers() {
    const { usersToSelect } = this.props;
    return this.getSelectedUsersCount() === usersToSelect;
  }

  private renderInstructions() {
    const { usersToSelect, seatsToRemove } = this.props;
    if (usersToSelect === null) {
      return <div className={styles.instructions}>Choose the user(s) below you want to remove from your account.</div>;
    }
    return <div className={styles.instructions}>
      You have chosen to remove <b>{seatsToRemove}</b> seat(s) from your account.
      <br />
      Choose <b>{usersToSelect}</b> user(s) below to remove from your account.
    </div>;
  }

  private renderContent() {
    const { loadingUsers } = this.props;
    const { selectedUsers } = this.state;
    if (loadingUsers) {
      return <LoadingOutlined />;
    }
    const disableSelectors = this.selectedEnoughUsers();
    return <div className={styles.table}>
      {selectedUsers.map(({ email, isOwner, isSelf, toBeRemoved, id }) => <RemoveSeatSelector
        id={id}
        email={email}
        isOwner={isOwner}
        isSelf={isSelf}
        toBeRemoved={toBeRemoved}
        handleSelection={this.toggleToBeRemoved}
        key={id}
        disabled={disableSelectors}
      />)}
    </div>;
  }

  private renderActions() {
    const { handleCancel, loadingUsers, usersToSelect } = this.props;
    const disableNext = !this.selectedEnoughUsers() && (usersToSelect !== null || this.getSelectedUsersCount() === 0);
    return <div className={cx(styles.row, styles.actions)}>
      <Button className={styles.button} type='ghost' onClick={handleCancel}>Cancel</Button>
      <Button className={styles.button} type='primary' disabled={disableNext} loading={loadingUsers} onClick={this.handleNextAction}>Continue</Button>
    </div>;
  }

  private renderEffectiveDate() {
    const { cycleEndDate } = this.props;
    const parsedDate = toMonthDayYearFormat(new Date(cycleEndDate * 1000));
    return <div className={cx(styles.supportingText, styles.regular)}>
      These users will continue to have access to SightX until your current billing cycle ends. They will be deactivated on <b>{parsedDate}.</b>
    </div>;
  }

  render(): ReactNode {
    const { visible, handleCancel } = this.props;
    return <Modal
      visible={visible}
      title="Remove Seats"
      className={styles.modal}
      footer={null}
      width={450}
      closeIcon={<Icon className={styles.closeIcon} component={CrossSVG} />}
      onCancel={handleCancel}
      maskClosable={false}
    >
      {this.renderInstructions()}
      {this.renderContent()}
      {this.renderEffectiveDate()}
      {this.renderActions()}
    </Modal>;
  }
}

const mapStateToProps = (state: RootState): IStateProps => {
  return {
    loadingUsers: userSelectors.getLoading(state),
    users: userSelectors.getList(state),
    loggedUserId: authSelectors.getLoggedInUserId(state)
  };
};

const dispatchProps: IDispatchProps = {
  getUsers: userActions.fetchUsersList
};

export const RemoveUsedSeatsModal = connect(mapStateToProps, dispatchProps)(RemoveUsedSeatsModalBase);